
import { useState } from 'react';
import InvestmentModal from '../../../components/feature/InvestmentModal';

const Investment = () => {
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const opportunities = [
    {
      title: 'Agricultural Processing Facility',
      location: 'Eastern Cape, South Africa',
      investment: '$2.5M - $5M',
      returns: '18-22% IRR',
      timeline: '24-36 months',
      description: 'Modern agro-processing facility serving 15 rural communities with direct farmer partnerships.',
      image: 'https://public.readdy.ai/ai/img_res/94a6160e01313472484c35e83aefe758.jpg',
      status: 'Active',
      detailedInfo: {
        overview: 'This state-of-the-art agricultural processing facility will serve as a central hub for 15 rural communities in the Eastern Cape, processing fruits, vegetables, and grains for both domestic and export markets. The facility will feature modern equipment, cold storage capabilities, and direct partnerships with over 500 local farmers.',
        marketAnalysis: 'The Eastern Cape region produces over 40% of South Africa\'s citrus fruits and has significant potential for vegetable processing. Current infrastructure limitations result in 30% post-harvest losses, representing a $50M annual opportunity. Our facility will capture this value while providing farmers with guaranteed markets and premium pricing.',
        financialProjections: {
          year1: '$800K revenue, 12% EBITDA margin',
          year3: '$3.2M revenue, 22% EBITDA margin',
          year5: '$6.8M revenue, 28% EBITDA margin',
          exitStrategy: 'Strategic sale to multinational food processor or IPO'
        },
        riskFactors: [
          'Weather and climate variability affecting crop yields',
          'Potential changes in export regulations or tariffs',
          'Competition from established processing facilities',
          'Infrastructure limitations in rural areas'
        ],
        keyBenefits: [
          'Direct partnerships with 500+ local farmers',
          'Government support through agricultural development programs',
          'Strong export market demand for processed African products',
          'Positive social impact creating 200+ direct jobs',
          'Scalable model for expansion to other regions',
          'ESG-compliant investment with measurable impact'
        ],
        nextSteps: [
          'Submit initial investment interest form',
          'Participate in virtual facility tour and management presentation',
          'Review detailed financial projections and legal documentation',
          'Conduct on-site due diligence visit',
          'Finalize investment terms and legal agreements'
        ]
      }
    },
    {
      title: 'Rural Retail Network',
      location: 'KwaZulu-Natal, South Africa',
      investment: '$1M - $3M',
      returns: '15-20% IRR',
      timeline: '18-24 months',
      description: 'Network of 8 modern grocery stores replacing traditional spaza shops in underserved communities.',
      image: 'https://readdy.ai/api/search-image?query=Chain%20of%20modern%20rural%20grocery%20stores%2C%20well-organized%20retail%20spaces%2C%20local%20customers%2C%20efficient%20supply%20chain%20operations%2C%20community-focused%20retail%20development&width=400&height=250&seq=invest-002&orientation=landscape',
      status: 'Funding',
      detailedInfo: {
        overview: 'A network of 8 modern grocery stores strategically located in underserved rural communities across KwaZulu-Natal. These stores will replace traditional spaza shops with modern retail infrastructure, improved product selection, and competitive pricing while creating local employment opportunities.',
        marketAnalysis: 'Rural KwaZulu-Natal has over 2.5 million residents with limited access to modern retail. Current spaza shops have limited product selection and higher prices due to inefficient supply chains. Our network will serve 150,000 people directly, capturing market share through better service and competitive pricing.',
        financialProjections: {
          year1: '$2.1M revenue across 8 stores, 8% net margin',
          year3: '$4.8M revenue, 15% net margin',
          year5: '$8.2M revenue, 18% net margin',
          exitStrategy: 'Acquisition by major South African retailer'
        },
        riskFactors: [
          'Competition from informal trading and established retailers',
          'Supply chain disruptions affecting product availability',
          'Economic downturns reducing consumer spending',
          'Regulatory changes affecting retail operations'
        ],
        keyBenefits: [
          'Serving underserved communities with essential goods',
          'Creating 120+ local jobs across 8 locations',
          'Proven retail model with strong unit economics',
          'Government support for rural development initiatives',
          'Scalable platform for regional expansion',
          'Strong community relationships and local partnerships'
        ],
        nextSteps: [
          'Review investment memorandum and financial projections',
          'Visit existing pilot store and meet management team',
          'Analyze market research and competitive landscape',
          'Participate in investor due diligence process',
          'Complete investment documentation and funding'
        ]
      }
    },
    {
      title: 'Renewable Energy Project',
      location: 'Limpopo, South Africa',
      investment: '$5M - $10M',
      returns: '20-25% IRR',
      timeline: '36-48 months',
      description: 'Solar and wind energy infrastructure providing power to rural communities and industrial facilities.',
      image: 'https://readdy.ai/api/search-image?query=Solar%20panel%20installation%20in%20rural%20African%20landscape%2C%20wind%20turbines%20on%20hills%2C%20renewable%20energy%20infrastructure%2C%20sustainable%20development%20project%2C%20clean%20energy%20technology&width=400&height=250&seq=invest-003&orientation=landscape',
      status: 'Planning',
      detailedInfo: {
        overview: 'A 50MW hybrid solar and wind energy project in Limpopo province, providing clean electricity to rural communities and industrial facilities. The project includes battery storage systems and will connect to the national grid while serving local energy needs.',
        marketAnalysis: 'Limpopo has excellent solar irradiation (2,500+ kWh/m²/year) and strong wind resources. The region faces frequent power outages, creating demand for reliable energy sources. Government renewable energy programs provide long-term power purchase agreements, ensuring stable revenue streams.',
        financialProjections: {
          year1: 'Construction phase, no revenue',
          year3: '$8.5M revenue, 35% EBITDA margin',
          year5: '$12.2M revenue, 42% EBITDA margin',
          exitStrategy: 'Sale to utility company or infrastructure fund'
        },
        riskFactors: [
          'Regulatory changes affecting renewable energy policies',
          'Grid connection delays or technical challenges',
          'Weather patterns affecting energy generation',
          'Currency fluctuation impacting equipment costs'
        ],
        keyBenefits: [
          'Long-term government power purchase agreements',
          'Excellent renewable energy resources in the region',
          'Positive environmental impact reducing carbon emissions',
          'Job creation during construction and operations',
          'Stable, predictable cash flows from energy sales',
          'Potential for carbon credit revenue streams'
        ],
        nextSteps: [
          'Review environmental and technical feasibility studies',
          'Examine power purchase agreements and regulatory approvals',
          'Assess construction timeline and contractor capabilities',
          'Evaluate financing structure and investment terms',
          'Complete environmental and social impact assessment'
        ]
      }
    },
    {
      title: 'Livestock Development Program',
      location: 'Free State, South Africa',
      investment: '$3M - $7M',
      returns: '16-21% IRR',
      timeline: '30-42 months',
      description: 'Comprehensive livestock breeding and processing program with local farmer partnerships.',
      image: 'https://readdy.ai/api/search-image?query=Modern%20livestock%20farming%20operation%20in%20rural%20Africa%2C%20cattle%20grazing%20in%20organized%20pastures%2C%20veterinary%20facilities%2C%20sustainable%20animal%20husbandry%20practices%2C%20agricultural%20development&width=400&height=250&seq=invest-004&orientation=landscape',
      status: 'Active',
      detailedInfo: {
        overview: 'A comprehensive livestock development program focusing on cattle and sheep breeding, processing, and marketing. The program includes modern breeding facilities, veterinary services, and processing plants, working in partnership with 200+ local farmers across the Free State.',
        marketAnalysis: 'The Free State is South Africa\'s largest livestock region, with growing domestic and export demand for quality meat products. Current infrastructure limitations and fragmented supply chains create opportunities for integrated operations. Export markets in the Middle East and Asia show strong growth potential.',
        financialProjections: {
          year1: '$1.8M revenue, 10% net margin',
          year3: '$5.4M revenue, 18% net margin',
          year5: '$9.8M revenue, 24% net margin',
          exitStrategy: 'Strategic acquisition by agribusiness conglomerate'
        },
        riskFactors: [
          'Disease outbreaks affecting livestock health',
          'Drought or climate conditions impacting feed costs',
          'Export market access and trade restrictions',
          'Fluctuating commodity prices for livestock products'
        ],
        keyBenefits: [
          'Partnership with 200+ experienced local farmers',
          'Integrated value chain from breeding to processing',
          'Strong export market demand for quality products',
          'Government support for livestock development',
          'Sustainable farming practices and animal welfare',
          'Significant job creation in rural communities'
        ],
        nextSteps: [
          'Tour existing facilities and meet farmer partners',
          'Review livestock breeding and processing operations',
          'Analyze export market opportunities and contracts',
          'Assess veterinary and quality control systems',
          'Finalize partnership agreements and investment terms'
        ]
      }
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active': return 'bg-green-100 text-green-800';
      case 'Funding': return 'bg-blue-100 text-blue-800';
      case 'Planning': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleLearnMore = (opportunity: any) => {
    setSelectedOpportunity(opportunity);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedOpportunity(null);
  };

  return (
    <>
      <section id="investment" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Current Investment Opportunities
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover carefully vetted investment opportunities that combine strong financial returns with positive community impact.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {opportunities.map((opportunity, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl overflow-hidden hover:shadow-lg transition-shadow duration-300">
                <div className="relative">
                  <img 
                    src={opportunity.image}
                    alt={opportunity.title}
                    className="w-full h-48 object-cover object-top"
                  />
                  <div className={`absolute top-4 right-4 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(opportunity.status)}`}>
                    {opportunity.status}
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{opportunity.title}</h3>
                  <div className="flex items-center text-gray-600 mb-4">
                    <i className="ri-map-pin-line mr-2"></i>
                    {opportunity.location}
                  </div>
                  
                  <p className="text-gray-600 mb-6 leading-relaxed">{opportunity.description}</p>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <div className="text-sm text-gray-500 mb-1">Investment Range</div>
                      <div className="font-semibold text-gray-900">{opportunity.investment}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">Expected Returns</div>
                      <div className="font-semibold text-emerald-600">{opportunity.returns}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">Timeline</div>
                      <div className="font-semibold text-gray-900">{opportunity.timeline}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">Status</div>
                      <div className="font-semibold text-gray-900">{opportunity.status}</div>
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => handleLearnMore(opportunity)}
                    className="w-full bg-emerald-600 text-white py-3 rounded-lg hover:bg-emerald-700 transition-colors duration-200 font-medium whitespace-nowrap cursor-pointer"
                  >
                    Learn More
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <div className="bg-emerald-50 rounded-2xl p-8">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                Ready to Invest in Rural Africa?
              </h3>
              <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                Join our network of forward-thinking investors who are creating positive change while achieving exceptional returns.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button className="bg-emerald-600 text-white px-8 py-4 rounded-lg hover:bg-emerald-700 transition-colors duration-200 font-semibold whitespace-nowrap cursor-pointer">
                  Schedule Consultation
                </button>
                <button className="border-2 border-emerald-600 text-emerald-600 px-8 py-4 rounded-lg hover:bg-emerald-600 hover:text-white transition-colors duration-200 font-semibold whitespace-nowrap cursor-pointer">
                  Download Investment Guide
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <InvestmentModal 
        isOpen={isModalOpen}
        onClose={closeModal}
        opportunity={selectedOpportunity}
      />
    </>
  );
};

export default Investment;
